/**
 * What the contract step must say instead of its normal content, in the order it is
 * decided.
 *
 * A refusal replaces the step: nothing on it can be acted on. `deciding` is weaker —
 * the answer that could refuse it has not arrived yet, so the step holds only the part
 * a refusal would speak for and leaves the rest, signature pad included, standing.
 */
export type ContractBlocker =
  | "deciding"
  | "no-priced-services"
  | "no-payment-plans"
  | null;

export interface ContractBlockerFacts {
  /** A quote, so it may still have no sellable service attached. */
  isProspection: boolean;
  /**
   * Signed in the portal, activated in the back office, or accepted. A prospection
   * contract still reports itself as one until a service lands on it, so without
   * this the unpriced refusal would lock its owner out of the contract they signed.
   */
  isLocked: boolean;
  /**
   * Fetching, not merely loading. A refetch keeps the previous data and reports
   * `isLoading` false, so keying on that trusted a list that was already known to be
   * out of date — which is what showed the unpriced refusal to a customer who had just
   * attached a service.
   */
  isFetchingServices: boolean;
  /**
   * The prospection flag itself comes from the contract, and attaching a service
   * invalidates both. The services answer usually lands first — the contract request
   * carries far more — so a stale "this is a quote" against a fresh "nothing to offer"
   * is the exact shape of that false refusal.
   */
  isFetchingContract: boolean;
  /**
   * react-query reports `isLoading` false on a failed query too, and telling the
   * customer their contractor forgot a price because a request blipped would send
   * them to support with the wrong story.
   */
  servicesFailed: boolean;
  /** The list came back, empty or not — as opposed to never having arrived. */
  hasLoadedServices: boolean;
  /** At least one service the company has priced for this address. */
  hasOfferableService: boolean;
  /**
   * Card on file only: no committed amount, so the payment-plan steps never render
   * and a missing plan is not something to refuse the contract over.
   */
  isCardCaptureOnly: boolean;
  isFetchingPaymentPlans: boolean;
  /** The plans came back and there are none to offer. */
  hasNoPaymentPlans: boolean;
}

export const getContractBlocker = ({
  isProspection,
  isLocked,
  isFetchingServices,
  isFetchingContract,
  servicesFailed,
  hasLoadedServices,
  hasOfferableService,
  isCardCaptureOnly,
  isFetchingPaymentPlans,
  hasNoPaymentPlans,
}: ContractBlockerFacts): ContractBlocker => {
  const canRefuseUnpriced = isProspection && !isLocked;
  const canRefuseNoPlans = !isCardCaptureOnly;

  // Held only for a request that could refuse *this* contract. Waiting on one that
  // could not leaves the customer watching a spinner for an answer that was never
  // going to change what they see.
  if (
    (canRefuseUnpriced && (isFetchingServices || isFetchingContract)) ||
    (canRefuseNoPlans && isFetchingPaymentPlans)
  ) {
    return "deciding";
  }

  if (
    canRefuseUnpriced &&
    !servicesFailed &&
    hasLoadedServices &&
    !hasOfferableService
  ) {
    return "no-priced-services";
  }

  if (canRefuseNoPlans && hasNoPaymentPlans) {
    return "no-payment-plans";
  }

  return null;
};
